import { MigratableJson } from '../MigratableJson';
import {
  CONTENT_BLOCK_NAME,
  IMAGE_NODE_NAME,
  VIDEO_NODE_NAME,
} from '@builder4/shared/utils/constants';

type BreakpointProps = Record<string, unknown>;

type LayoutKey =
  | 'flexDirection'
  | 'width'
  | 'height'
  | 'fillSpace'
  | 'flexBasis'
  | 'maxWidth'
  | 'maxWidthUnit';

type DefaultLayoutByDisplayName = Record<
  string,
  { tablet?: BreakpointProps; mobile?: BreakpointProps }
>;

/**
 * Per-component default props; tablet/mobile are used to reset layout keys
 * (flexDirection, width, height, fillSpace, flexBasis, maxWidth, maxWidthUnit)
 * after stripping. Container has mobile only; Image/Video have mobile layout defaults.
 */
const DEFAULT_LAYOUT_BY_DISPLAY_NAME: DefaultLayoutByDisplayName = {
  [CONTENT_BLOCK_NAME]: {
    tablet: {
      padding: [20, 20, 20, 20],
    },
    mobile: {
      padding: [10, 10, 10, 10],
      flexDirection: 'column',
      width: '100%',
    },
  },
  [IMAGE_NODE_NAME]: {
    tablet: {},
    mobile: {
      width: '100%',
      fillSpace: 'no',
      flexBasis: '100%',
    },
  },
  [VIDEO_NODE_NAME]: {
    tablet: {},
    mobile: {
      width: '100%',
      fillSpace: 'no',
      flexBasis: '100%',
    },
  },
};

/**
 * Layout-related props to remove from tablet and mobile breakpoint overrides.
 * Removing these from old designs fixes broken responsiveness by letting
 * components use consistent default layout on smaller breakpoints.
 */
const LAYOUT_PROPS_TO_STRIP: readonly LayoutKey[] = [
  'flexDirection',
  'width',
  'height',
  'fillSpace',
  'flexBasis',
  'maxWidth',
  'maxWidthUnit',
];

/**
 * Migration v12: Strip layout props from tablet and mobile breakpoints
 *
 * Old designs may have layout-specific overrides (flexDirection, width, height,
 * fillSpace, flexBasis, maxWidth, maxWidthUnit) on tablet and mobile that cause
 * broken responsiveness. This migration removes those props from props.tablet
 * and props.mobile for every node. For node types that define default
 * tablet/mobile layout in their defaultProps (e.g. Container mobile:
 * flexDirection/width; Image/Video mobile: width/fillSpace/flexBasis), the
 * migration then sets those default layout values on the breakpoint. All other
 * props (e.g. padding, backgroundColor) are unchanged. Desktop props are not
 * modified.
 */
export default function toV12<T extends MigratableJson>(data: T): T {
  const allNodes = data.getAllNodes();

  for (const { node } of allNodes) {
    const props = node.props;
    if (!props || typeof props !== 'object') {
      continue;
    }

    const displayName = node.displayName as string;
    const defaults = DEFAULT_LAYOUT_BY_DISPLAY_NAME[displayName];
    const tabletDefaultProps: BreakpointProps | undefined = defaults?.tablet;
    const mobileDefaultProps: BreakpointProps | undefined = defaults?.mobile;

    const hasTablet = props.tablet && typeof props.tablet === 'object';
    const hasMobile = props.mobile && typeof props.mobile === 'object';
    if (!hasTablet && !hasMobile) {
      continue;
    }

    const nextProps: Record<string, unknown> = { ...props };

    if (hasTablet) {
      const tablet: BreakpointProps = { ...(nextProps.tablet as BreakpointProps) };
      for (const key of LAYOUT_PROPS_TO_STRIP) {
        delete tablet[key];
        if (tabletDefaultProps != null && key in tabletDefaultProps) {
          tablet[key] = tabletDefaultProps[key];
        }
      }
      nextProps.tablet = tablet;
    }

    if (hasMobile) {
      const mobile: BreakpointProps = { ...(nextProps.mobile as BreakpointProps) };
      for (const key of LAYOUT_PROPS_TO_STRIP) {
        delete mobile[key];
        if (mobileDefaultProps != null && key in mobileDefaultProps) {
          mobile[key] = mobileDefaultProps[key];
        }
      }
      nextProps.mobile = mobile;
    }

    node.props = nextProps;
  }

  return data;
}
